// Dashboard Data Service - Fetches real data from localStorage
import { uploadHistoryManager, uploadSessionManager } from './uploadStorage';

/**
 * Dashboard metrics calculation service
 */
export const dashboardDataService = {
  // Calculate real metrics from stored data
  calculateRealMetrics: () => {
    try {
      const uploadHistory = uploadHistoryManager?.getHistory();
      const currentSession = uploadSessionManager?.loadSession();
      
      // Calculate total products
      let totalProducts = 0;
      let successfulUploads = 0;
      let failedUploads = 0;
      let pendingProcesses = 0;
      
      // Aggregate data from upload history
      uploadHistory?.forEach(entry => {
        if (entry?.details) {
          totalProducts += entry?.details?.total || 0;
          successfulUploads += entry?.details?.successful || 0;
          failedUploads += entry?.details?.failed || 0;
          
          // Count pending processes
          if (entry?.status === 'processing' || entry?.status === 'pending') {
            pendingProcesses += 1;
          }
        }
      });
      
      // Add current session data if exists
      if (currentSession?.uploadData) {
        const sessionTotal = currentSession?.uploadData?.length || 0;
        totalProducts += sessionTotal;
        
        if (currentSession?.status === 'processing') {
          pendingProcesses += 1;
        }
      }
      
      return {
        totalProducts: totalProducts || 0,
        successfulUploads: successfulUploads || 0,
        failedUploads: failedUploads || 0,
        pendingProcesses: pendingProcesses || 0
      };
    } catch (error) {
      console.error('Error calculating real metrics:', error);
      // Return zero values if calculation fails
      return {
        totalProducts: 0,
        successfulUploads: 0,
        failedUploads: 0,
        pendingProcesses: 0
      };
    }
  },

  // Get real activity data from upload history
  getRealActivityData: () => {
    try {
      const uploadHistory = uploadHistoryManager?.getHistory();
      const now = Date.now();
      // Filtrar actividades con 0 productos y más de 1 hora de antigüedad
      const filtered = uploadHistory?.filter(entry => {
        const total = entry?.details?.total || 0;
        const ts = new Date(entry?.date || entry?.addedAt).getTime();
        // Si tiene 0 productos y es más viejo de 1 hora, no mostrar
        if (total === 0 && (now - ts) > 60 * 60 * 1000) return false;
        return true;
      });
      // Convertir a formato de actividad
      const activities = filtered?.slice(0, 10)?.map(entry => ({
        id: entry?.id,
        type: entry?.type || 'bulk_upload',
        title: entry?.title || `Carga de ${entry?.details?.total || 0} productos`,
        description: entry?.description || `${entry?.details?.successful || 0} exitosos, ${entry?.details?.failed || 0} fallidos`,
        status: entry?.status || 'completed',
        timestamp: new Date(entry?.date || entry?.addedAt),
        details: {
          total: entry?.details?.total || 0,
          successful: entry?.details?.successful || 0,
          failed: entry?.details?.failed || 0
        }
      }));
      return activities || [];
    } catch (error) {
      console.error('Error getting real activity data:', error);
      return [];
    }
  },

  // Calculate monthly trends
  calculateTrends: () => {
    try {
      const uploadHistory = uploadHistoryManager?.getHistory();
      const now = new Date();
      const lastMonth = new Date(now?.getFullYear(), now?.getMonth() - 1, 1);
      const thisMonth = new Date(now?.getFullYear(), now?.getMonth(), 1);
      
      // Separate this month and last month data
      const thisMonthEntries = uploadHistory?.filter(entry => 
        new Date(entry?.date) >= thisMonth
      );
      
      const lastMonthEntries = uploadHistory?.filter(entry => {
        const entryDate = new Date(entry?.date);
        return entryDate >= lastMonth && entryDate < thisMonth;
      });
      
      // Calculate totals
      const thisMonthTotal = thisMonthEntries?.reduce((sum, entry) => 
        sum + (entry?.details?.total || 0), 0
      );
      
      const lastMonthTotal = lastMonthEntries?.reduce((sum, entry) => 
        sum + (entry?.details?.total || 0), 0
      );
      
      const thisMonthSuccessful = thisMonthEntries?.reduce((sum, entry) => 
        sum + (entry?.details?.successful || 0), 0
      );
      
      const lastMonthSuccessful = lastMonthEntries?.reduce((sum, entry) => 
        sum + (entry?.details?.successful || 0), 0
      );
      
      const thisMonthFailed = thisMonthEntries?.reduce((sum, entry) => 
        sum + (entry?.details?.failed || 0), 0
      );
      
      const lastMonthFailed = lastMonthEntries?.reduce((sum, entry) => 
        sum + (entry?.details?.failed || 0), 0
      );
      
      // Calculate percentage changes
      const calculateTrend = (current, previous) => {
        if (previous === 0 && current > 0) return { trend: 'up', value: '+100%' };
        if (previous === 0 && current === 0) return { trend: 'neutral', value: '0%' };
        if (current === 0 && previous > 0) return { trend: 'down', value: '-100%' };
        
        const percentage = Math.round(((current - previous) / previous) * 100);
        const trend = percentage > 0 ? 'up' : percentage < 0 ? 'down' : 'neutral';
        const value = percentage > 0 ? `+${percentage}%` : `${percentage}%`;
        
        return { trend, value };
      };
      
      const totalTrend = calculateTrend(thisMonthTotal, lastMonthTotal);
      const successfulTrend = calculateTrend(thisMonthSuccessful, lastMonthSuccessful);
      const failedTrend = calculateTrend(thisMonthFailed, lastMonthFailed);
      
      return {
        totalProducts: totalTrend,
        successfulUploads: successfulTrend,
        failedUploads: failedTrend,
        pendingProcesses: { trend: 'neutral', value: '0%' } // Pending processes don't have historical comparison
      };
    } catch (error) {
      console.error('Error calculating trends:', error);
      return {
        totalProducts: { trend: 'neutral', value: '0%' },
        successfulUploads: { trend: 'neutral', value: '0%' },
        failedUploads: { trend: 'neutral', value: '0%' },
        pendingProcesses: { trend: 'neutral', value: '0%' }
      };
    }
  },

  // Get system connection status
  getSystemStatus: () => {
    try {
      // Verificar si tenemos credenciales de MercadoLibre válidas
      const mercadoLibreAppId = import.meta.env?.VITE_MERCADOLIBRE_APP_ID || localStorage.getItem('mercadolibre_app_id');
      const mercadoLibreSecret = import.meta.env?.VITE_MERCADOLIBRE_CLIENT_SECRET || localStorage.getItem('mercadolibre_client_secret');
      const mercadoLibreToken = import.meta.env?.VITE_MERCADOLIBRE_ACCESS_TOKEN || localStorage.getItem('mercadolibre_access_token');
      
      // Verificar conexión API basada en credenciales guardadas
      const apiConnected = !!(mercadoLibreAppId && mercadoLibreSecret);
      const tokenAvailable = !!mercadoLibreToken;
      
      // Verificar última sincronización
      const lastSync = localStorage.getItem('mercadolibre_last_sync');
      
      return {
        apiConnected: apiConnected,
        dataSync: tokenAvailable,
        lastSync: lastSync ? new Date(lastSync) : null,
        mercadoLibreApi: {
          connected: apiConnected,
          tokenValid: tokenAvailable,
          lastValidation: localStorage.getItem('mercadolibre_last_validation')
        }
      };
    } catch (error) {
      console.error('Error obteniendo el estado del sistema:', error);
      // Devuelve valores predeterminados en caso de error
      return {
        apiConnected: false,
        dataSync: false,
        lastSync: null,
        mercadoLibreApi: {
          connected: false,
          tokenValid: false,
          lastValidation: null
        }
      };
    }
  },

  // Get recent notifications based on real data
  getRealNotifications: () => {
    try {
      const uploadHistory = uploadHistoryManager?.getHistory();
      const notifications = [];
      
      // Check for MercadoLibre API connection status
      const lastValidation = localStorage.getItem('mercadolibre_last_validation');
      const accessToken = localStorage.getItem('mercadolibre_access_token');
      
      if (lastValidation && accessToken) {
        const validationDate = new Date(lastValidation);
        const isRecent = validationDate > new Date(Date.now() - 60 * 60 * 1000); // Last hour
        
        if (isRecent) {
          notifications.push({
            id: 'mercadolibre_connected',
            type: 'success',
            title: 'MercadoLibre API conectada',
            message: 'Credenciales validadas correctamente',
            timestamp: validationDate,
            priority: 'medium'
          });
        }
      }
      
      // Check for failed uploads
      const recentFailures = uploadHistory?.filter(entry => 
        entry?.status === 'failed' && 
        entry?.details?.failed > 0 &&
        new Date(entry?.date) > new Date(Date.now() - 24 * 60 * 60 * 1000) // Last 24 hours
      );
      
      recentFailures?.forEach(failure => {
        notifications?.push({
          id: `failure_${failure?.id}`,
          type: 'error',
          title: 'Carga fallida detectada',
          message: `${failure?.details?.failed} productos fallaron en la carga`,
          timestamp: new Date(failure?.date),
          priority: 'high'
        });
      });
      
      // Check for successful large uploads
      const recentSuccesses = uploadHistory?.filter(entry => 
        entry?.status === 'completed' && 
        entry?.details?.successful > 50 &&
        new Date(entry?.date) > new Date(Date.now() - 24 * 60 * 60 * 1000) // Last 24 hours
      );
      
      recentSuccesses?.forEach(success => {
        notifications?.push({
          id: `success_${success?.id}`,
          type: 'success',
          title: 'Carga masiva completada',
          message: `${success?.details?.successful} productos cargados exitosamente`,
          timestamp: new Date(success?.date),
          priority: 'medium'
        });
      });
      
      // Sort by timestamp (newest first)
      return notifications?.sort((a, b) => 
        new Date(b?.timestamp) - new Date(a?.timestamp)
      )?.slice(0, 5); // Return only 5 most recent
      
    } catch (error) {
      console.error('Error getting real notifications:', error);
      return [];
    }
  }
};

export default dashboardDataService;